import { cn } from "@/lib/utils";
import { Leaddrop9CircleBg } from "@/widgets/leaddrop/leaddrop9/Leaddrop9CircleBg";
import type { LeadFormSkinDefinition } from "./types";

/**
 * Skin 3000 — the Lead Drop 9 look.
 *
 * Typography, spacing and the breakpoint ladder are lifted verbatim from
 * `widgets/leaddrop/leaddrop9/Leaddrop9.tsx` so a config-driven form and the hand-built
 * variant 9 are visually indistinguishable side by side. That matters more than it
 * sounds: these two will run on the same site during the migration, and a visitor who
 * meets both must not see two different products.
 *
 * ─── Parity is per SCREEN, not per card ─────────────────────────────────────────────
 * A first pass matched the collect step and then invented the other two, which is the
 * easy mistake: those screens are only reachable after filling a form in, so they are
 * rarely looked at. They are not variations on the card —
 *
 *   · the OTP gate is two SENTENCES with links inside them wrapped around a row of four
 *     single-digit boxes. Not one wide code field, and the resend is not a button.
 *   · the ending REPLACES the card: its own padding, its own 560px floor, a gradient
 *     circle bleeding past the top edge, an overlapping illustration, bottom-aligned
 *     words and an outlined pill CTA.
 *
 * The class keys below are shaped around those facts, which is why the skin contract
 * carries `otpBoxes`/`otpResend`/`renderBackdrop` rather than a generic `otpInput`.
 *
 * ─── The odd breakpoints are deliberate ─────────────────────────────────────────────
 * Leaddrop9 uses `min-[1080px]` / `min-[1230px]` / `min-[1430px]` / `min-[1750px]` rather
 * than Tailwind's defaults, because it was cut from a design with those exact frames. Do
 * not "tidy" them to sm/md/lg; they would stop matching the widget they are copied from.
 *
 * Every colour is a CSS variable fed from the CMS settings below, so an author configures
 * this skin exactly the way they already configure lead form variant 9 — same knob names,
 * same defaults.
 */

const FIELD_TEXT = cn(
  "font-poppins font-normal leading-[1.55]",
  "text-[14px] tracking-[0.07px]",
  "min-[1430px]:text-[15px] min-[1430px]:tracking-[0.075px]",
  "min-[1750px]:text-[16px] min-[1750px]:tracking-[0.08px]",
);

/** Leaddrop9's LAST_BODY_CLASS — the terminal screens' body copy. */
const LAST_BODY = cn(
  "font-poppins font-normal leading-[1.55] text-center",
  "text-[14px] tracking-[0.07px]",
  "min-[1430px]:text-[15px] min-[1430px]:tracking-[0.075px]",
  "min-[1750px]:text-[16px] min-[1750px]:tracking-[0.08px]",
);

/** The card's own padding ladder, shared by the embedded card and the popup shell. */
const CARD_PADDING = cn(
  "px-[12px] py-[20px]",
  "min-[1080px]:px-[20px] min-[1080px]:py-[16px]",
  "min-[1230px]:px-[24px] min-[1230px]:py-[20px]",
);

const CARD_CHROME = cn(
  "relative flex w-full flex-col rounded-[12px] border",
  "border-[var(--lf-card-border)] bg-[var(--lf-card-bg)]",
);

/** Leaddrop9's PRIMARY. Kept as the default so a fresh form looks like the shipped one. */
const PRIMARY = "#9F1C34";
const INK = "#101112";
/** Leaddrop9's muted grey — the OTP description and the resend sentence. */
const MUTED = "#888888";

const skin3000: LeadFormSkinDefinition = {
  id: "3000",
  name: "Skin 3000 — Lead Drop 9",
  description:
    "Bordered white card, Poppins, centred header, full-width CTA and a consent footnote.",
  surfaces: ["fixed", "popup"],

  settingGroups: [
    {
      title: "Header",
      settings: [
        {
          key: "form_title",
          label: "Title",
          type: "text",
          store: "copy",
          defaultValue: "Book Your Seat Now",
          placeholder: "Book Your Seat Now",
        },
        { key: "form_title_color", label: "Title colour", type: "color", store: "theme", defaultValue: INK },
        {
          key: "form_sub_text",
          label: "Sub text",
          type: "textarea",
          store: "copy",
          defaultValue: "Get a call back from our admissions team",
        },
        { key: "form_sub_text_color", label: "Sub text colour", type: "color", store: "theme", defaultValue: INK },
        {
          key: "show_step_indicator",
          label: "Show “Step 2 of 3”",
          type: "toggle",
          store: "theme",
          defaultValue: true,
          help: "Only ever shown when the form has more than one field step.",
        },
      ],
    },
    {
      title: "Card & fields",
      settings: [
        { key: "card_background_color", label: "Card background", type: "color", store: "theme", defaultValue: "#FFFFFF" },
        { key: "card_border_color", label: "Card border", type: "color", store: "theme", defaultValue: "#BCD0E0" },
        { key: "label_color", label: "Field label colour", type: "color", store: "theme", defaultValue: INK },
        { key: "field_border_color", label: "Field border", type: "color", store: "theme", defaultValue: "#C5C5C5" },
        { key: "field_text_color", label: "Field text colour", type: "color", store: "theme", defaultValue: "#222222" },
      ],
    },
    {
      title: "Buttons",
      settings: [
        { key: "button_background_color", label: "Button background", type: "color", store: "theme", defaultValue: PRIMARY },
        { key: "button_text_color", label: "Button text colour", type: "color", store: "theme", defaultValue: "#FFFFFF" },
        {
          key: "button_step_1_text",
          label: "Continue label",
          type: "text",
          store: "copy",
          defaultValue: "Continue",
          help: "Shown on every step except the last.",
        },
        {
          key: "submit_text",
          label: "Submit label",
          type: "text",
          store: "copy",
          defaultValue: "Sign Up",
          help: "Shown on the final step.",
        },
        {
          key: "skip_text",
          label: "Skip label",
          type: "text",
          store: "copy",
          defaultValue: "Skip",
          help: "Only rendered on steps the author marked optional.",
        },
      ],
    },
    {
      title: "OTP step",
      settings: [
        {
          key: "otp_description_text",
          label: "Sent-to line",
          type: "text",
          store: "copy",
          defaultValue: "We’ve sent a 4-digit OTP to",
          help: "The masked number is appended automatically, followed by the change-number link.",
        },
        {
          key: "otp_description_text_color",
          label: "Sent-to line colour",
          type: "color",
          store: "theme",
          defaultValue: MUTED,
        },
        { key: "edit_number_text", label: "Change-number link", type: "text", store: "copy", defaultValue: "Change Number" },
        {
          key: "edit_number_text_color",
          label: "Change-number link colour",
          type: "color",
          store: "theme",
          defaultValue: PRIMARY,
        },
        {
          key: "otp_header_text",
          label: "Code heading",
          type: "text",
          store: "copy",
          defaultValue: "Enter 4-digit OTP Here",
        },
        { key: "otp_header_text_color", label: "Code heading colour", type: "color", store: "theme", defaultValue: "#222222" },
        {
          key: "resend_prefix_text",
          label: "Resend sentence",
          type: "text",
          store: "copy",
          defaultValue: "Didn’t receive the OTP?",
          help: "The resend link and its countdown finish this sentence.",
        },
        { key: "resend_otp_text", label: "Resend link", type: "text", store: "copy", defaultValue: "Resend OTP" },
        { key: "resend_otp_text_color", label: "Resend link colour", type: "color", store: "theme", defaultValue: PRIMARY },
        { key: "button_step_2_text", label: "Verify label", type: "text", store: "copy", defaultValue: "Verify & Continue" },
        {
          key: "button_step_2_background_color",
          label: "Verify button background",
          type: "color",
          store: "theme",
          defaultValue: PRIMARY,
        },
        { key: "button_step_2_text_color", label: "Verify button text", type: "color", store: "theme", defaultValue: "#FFFFFF" },
      ],
    },
    {
      title: "Consent",
      settings: [
        {
          key: "checkbox_description",
          label: "Consent text",
          type: "textarea",
          store: "copy",
          defaultValue:
            "By signing up you agree to our {{1:Terms & Conditions}} and {{2:Privacy Policy}}.",
          help: "Use {{1:Label}} and {{2:Label}} to link to the two URLs below.",
        },
        { key: "checkbox_description_color", label: "Consent text colour", type: "color", store: "theme", defaultValue: INK },
        { key: "checkbox_color", label: "Consent link colour", type: "color", store: "theme", defaultValue: PRIMARY },
        { key: "terms_conditions_link", label: "Terms & Conditions URL", type: "url", store: "theme", defaultValue: "" },
        { key: "privacy_policy_link", label: "Privacy Policy URL", type: "url", store: "theme", defaultValue: "" },
      ],
    },
    {
      title: "Popup layout",
      settings: [
        {
          key: "popup_desktop_image",
          label: "Popup image (desktop)",
          type: "image",
          store: "theme",
          defaultValue: "",
          help: "Optional. Adds a full-height image rail beside the fields and widens the dialog. Leave blank for lead form 9's single-column popup.",
          surfaces: ["popup"],
        },
        {
          key: "popup_mobile_image",
          label: "Popup image (mobile)",
          type: "image",
          store: "theme",
          defaultValue: "",
          help: "Shown below 1080px, where the rail becomes a strip above the fields. Falls back to the desktop image when blank.",
          surfaces: ["popup"],
        },
        {
          key: "popup_image_alt",
          label: "Popup image alt text",
          type: "text",
          store: "copy",
          defaultValue: "",
          surfaces: ["popup"],
        },
      ],
    },
    {
      title: "After submit — shared",
      settings: [
        {
          key: "success_circle_color",
          label: "Backdrop circle",
          type: "color",
          store: "theme",
          defaultValue: "#B75265",
          help: "The gradient arc behind every ending screen.",
        },
        {
          key: "course_text_color",
          label: "Course name colour",
          type: "color",
          store: "theme",
          defaultValue: PRIMARY,
          help: "Colour of {course} where it appears in the ending copy — e.g. “Thank you for your interest in {course}”.",
        },
        {
          key: "enable_application_redirect",
          label: "Send the button to the course's application page",
          type: "toggle",
          store: "copy",
          defaultValue: false,
          help: "Uses the POK of the course the visitor picked. Falls back to the link below when the course has no POK, or the form collects no course.",
        },
        {
          key: "application_redirect_path",
          label: "Application path",
          type: "text",
          store: "copy",
          defaultValue: "/application/{slug}",
          placeholder: "/application/{slug}",
          help: "{slug} is replaced with the selected course's POK — /application/{slug} becomes /application/POK1.",
        },
        {
          key: "success_button_link",
          label: "Button destination",
          type: "url",
          store: "theme",
          defaultValue: "",
          help: "Opens in a new tab. Blank closes the popup, or returns to the homepage on an embedded form.",
        },
      ],
    },
    {
      title: "After submit — new user",
      settings: [
        {
          key: "new_user_title", label: "Title", type: "text", store: "copy", defaultValue: "Thank You",
          help: "Use {course} to insert the programme the visitor chose — it takes the “Course name colour”.",
        },
        {
          key: "new_user_subtitle",
          label: "Subtitle",
          type: "text",
          store: "copy",
          defaultValue: "We’ve Received Your Details!",
          help:
            "Use {course} to insert the programme the visitor chose — it takes the “Course name colour” below."
        },
        {
          key: "new_user_description",
          label: "Message",
          type: "textarea",
          store: "copy",
          defaultValue: "Our team will be in touch shortly.",
          help:
            "Use {course} to insert the programme the visitor chose — it takes the “Course name colour” below."
        },
        { key: "new_user_button_text", label: "Button label", type: "text", store: "copy", defaultValue: "Back to Website" },
        { key: "new_user_title_color", label: "Title colour", type: "color", store: "theme", defaultValue: INK },
        { key: "new_user_subtitle_color", label: "Subtitle colour", type: "color", store: "theme", defaultValue: INK },
        { key: "new_user_description_color", label: "Message colour", type: "color", store: "theme", defaultValue: INK },
        { key: "new_user_button_text_color", label: "Button colour", type: "color", store: "theme", defaultValue: PRIMARY },
        { key: "new_user_background_image", label: "Image", type: "image", store: "theme", defaultValue: "" },
      ],
    },
    {
      title: "After submit — returning user",
      settings: [
        {
          key: "existing_user_title", label: "Title", type: "text", store: "copy", defaultValue: "Welcome Back!",
          help: "Use {course} to insert the programme the visitor chose — it takes the “Course name colour”.",
        },
        {
          key: "existing_user_subtitle",
          label: "Subtitle",
          type: "text",
          store: "copy",
          defaultValue: "You’re Already Registered",
          help: "Shown when the verified mobile already existed — not a new lead.",
        },
        {
          key: "existing_user_description",
          label: "Message",
          type: "textarea",
          store: "copy",
          defaultValue: "",
          help:
            "Use {course} to insert the programme the visitor chose — it takes the “Course name colour” below."
        },
        {
          key: "existing_user_button_text",
          label: "Button label",
          type: "text",
          store: "copy",
          defaultValue: "Back to Website",
        },
        { key: "existing_user_title_color", label: "Title colour", type: "color", store: "theme", defaultValue: INK },
        { key: "existing_user_subtitle_color", label: "Subtitle colour", type: "color", store: "theme", defaultValue: INK },
        { key: "existing_user_description_color", label: "Message colour", type: "color", store: "theme", defaultValue: INK },
        { key: "existing_user_button_text_color", label: "Button colour", type: "color", store: "theme", defaultValue: PRIMARY },
        { key: "existing_user_background_image", label: "Image", type: "image", store: "theme", defaultValue: "" },
      ],
    },
    {
      title: "After submit — brochure download",
      settings: [
        {
          key: "download_title",
          label: "Title",
          type: "text",
          store: "copy",
          defaultValue: "Downloaded Successfully",
          help: "Shown when the form was opened by a brochure CTA.",
        },
        {
          key: "download_subtitle", label: "Subtitle", type: "text", store: "copy", defaultValue: "Review it carefully",
          help: "Use {course} to insert the programme the visitor chose — it takes the “Course name colour”.",
        },
        {
          key: "download_description",
          label: "Message",
          type: "textarea",
          store: "copy",
          defaultValue:
            "Your download will begin automatically. You can {{click here}} to download it manually.",
          help: "Wrap words in {{ }} to make them the manual download link.",
        },
        { key: "download_title_color", label: "Title colour", type: "color", store: "theme", defaultValue: INK },
        { key: "download_subtitle_color", label: "Subtitle colour", type: "color", store: "theme", defaultValue: INK },
        { key: "download_description_color", label: "Message colour", type: "color", store: "theme", defaultValue: INK },
        { key: "brochure_link_color", label: "Download link colour", type: "color", store: "theme", defaultValue: PRIMARY },
        { key: "download_background_image", label: "Image", type: "image", store: "theme", defaultValue: "" },
      ],
    },
  ],

  cssVars: (v) =>
    ({
      "--lf-primary": v.button_background_color || PRIMARY,
      "--lf-on-primary": v.button_text_color || "#FFFFFF",
      "--lf-primary-2": v.button_step_2_background_color || v.button_background_color || PRIMARY,
      "--lf-on-primary-2": v.button_step_2_text_color || "#FFFFFF",
      "--lf-card-bg": v.card_background_color || "#FFFFFF",
      "--lf-card-border": v.card_border_color || "#BCD0E0",
      "--lf-field-border": v.field_border_color || "#C5C5C5",
      "--lf-field-text": v.field_text_color || "#222222",
      "--lf-label": v.label_color || INK,
      "--lf-title": v.form_title_color || INK,
      "--lf-subtitle": v.form_sub_text_color || INK,
      "--lf-consent": v.checkbox_description_color || INK,
      "--lf-consent-link": v.checkbox_color || PRIMARY,
      "--lf-otp-desc": v.otp_description_text_color || MUTED,
      "--lf-otp-header": v.otp_header_text_color || "#222222",
      "--lf-edit-number": v.edit_number_text_color || PRIMARY,
      "--lf-resend": v.resend_otp_text_color || PRIMARY,
    }) as React.CSSProperties,

  /**
   * The circle is anchored above the card and clipped by it, so it must be the FIRST
   * child and everything after it needs a stacking context — see `successImage` /
   * `successContent`, both of which carry `z-[1]` for exactly that reason.
   */
  renderBackdrop: (v) => <Leaddrop9CircleBg color={v.success_circle_color || "#B75265"} />,

  classes: {
    /* ── Embedded: the single-column card, straight from LeaddropForm9 ──────────── */
    card: cn(
      CARD_CHROME,
      CARD_PADDING,
      "mx-auto max-w-[360px] min-[1080px]:max-w-[380px] min-[1230px]:max-w-[400px] min-[1430px]:max-w-[420px] min-[1750px]:max-w-[480px]",
      // Floor ≈ step 1's natural height, so the shorter OTP step does not shrink the card
      // and shift the page around it.
      "min-h-[500px] min-[1430px]:min-h-[525px] min-[1750px]:min-h-[545px]",
    ),
    cardInner: "flex w-full flex-1 flex-col gap-[16px] min-[1080px]:gap-[20px]",

    /* ── Popup: the same card, capped to the viewport ────────────────────────────
     * Lead form 9's popup is NOT a wider composition — it is the embedded card with a
     * close button and a height cap. The image rail below is an addition this skin
     * offers, and it only changes the shell when an author actually configures one. */
    popupShell: cn(
      CARD_CHROME,
      CARD_PADDING,
      // See skin3001: a flex child without `shrink-0` is squeezed below its declared width.
      "shrink-0",
      "mx-auto w-[calc(100vw-2rem)] max-w-[360px] min-[1080px]:max-w-[380px] min-[1230px]:max-w-[400px] min-[1430px]:max-w-[420px] min-[1750px]:max-w-[480px]",
      "min-h-[min(500px,calc(100dvh-2rem))] min-[1430px]:min-h-[min(525px,calc(100dvh-2rem))] min-[1750px]:min-h-[min(545px,calc(100dvh-2rem))]",
      "max-h-[calc(100dvh-2rem)] overflow-y-auto",
    ),
    /** Applied on top of `popupShell` only when an image rail is configured. */
    popupShellWithAside: "sm:w-fit sm:max-w-[calc(100vw-2rem)] min-[1080px]:max-w-[calc(100vw-2rem)] min-[1230px]:max-w-[calc(100vw-2rem)] min-[1430px]:max-w-[calc(100vw-2rem)] min-[1750px]:max-w-[calc(100vw-2rem)]",
    popupInner: cn(
      "flex min-w-0 flex-1 flex-col gap-[18px]",
      // Side-by-side only once there is room; below that the rail stacks above the form.
      "sm:flex-row sm:items-stretch sm:gap-8",
    ),
    popupAside: cn(
      "shrink-0 overflow-hidden rounded-xl",
      "h-[134px] w-full",
      "sm:h-auto sm:w-[235px] md:w-[270px]",
    ),
    popupAsideImage: "h-full w-full object-cover",
    popupColumn: "flex min-w-0 flex-1 flex-col gap-[16px] min-[1080px]:gap-[20px]",
    popupClose: "absolute right-[20px] top-[20px] z-10 flex size-[24px] cursor-pointer items-center justify-center",

    header: "flex w-full flex-col gap-[8px]",
    title: cn(
      "font-poppins font-semibold leading-[1.3] text-center text-[var(--lf-title)]",
      "text-[24px] tracking-[-0.24px]",
      "min-[1430px]:text-[28px] min-[1430px]:tracking-[-0.28px]",
      "min-[1750px]:text-[32px] min-[1750px]:tracking-[-0.32px]",
    ),
    subtitle: cn(
      "font-poppins font-normal leading-[1.55] text-center text-[var(--lf-subtitle)]",
      "text-[14px] tracking-[0.07px]",
      "min-[1430px]:text-[15px] min-[1430px]:tracking-[0.075px]",
      "min-[1750px]:text-[16px] min-[1750px]:tracking-[0.08px]",
    ),
    body: "grid grid-cols-12 gap-x-[12px] gap-y-[12px]",
    label: "mb-[6px] block font-poppins text-[12px] font-medium leading-[1.5] text-[var(--lf-label)]",
    requiredMark: "ml-0.5 text-[var(--lf-primary)]",
    // Border and focus colour resolve through variables so `fieldInvalid` can REDECLARE
    // them rather than add a second `border-*`/`focus:border-*` to the same element. Two
    // utilities for one property are resolved by the order Tailwind emits them, not by the
    // order they are composed — so the red invalid border was winning only by luck.
    field: cn(
      FIELD_TEXT,
      "w-full rounded-[7px] border bg-white px-[12px] py-[12px] text-[var(--lf-field-text)]",
      "border-[var(--lf-field-stroke,var(--lf-field-border))]",
      "outline-none transition-colors placeholder:text-[#8a8a8a]",
      "focus:border-[var(--lf-field-focus,var(--lf-primary))] focus:ring-1 focus:ring-[var(--lf-field-focus,var(--lf-primary))]",
      "disabled:bg-[#f5f5f5] disabled:text-[#9a9a9a]",
    ),
    fieldInvalid: "[--lf-field-stroke:#ef4444] [--lf-field-focus:#ef4444]",
    errorText: "mt-[4px] font-poppins text-[12px] leading-[1.4] text-red-500",
    helpText: "mt-[4px] font-poppins text-[11px] leading-[1.4] text-[#6b6b6b]",
    // Pinned to the card bottom so the CTA sits at the end on every step regardless of
    // how tall that step's body is — Leaddrop9's footer is `mt-auto` for the same reason.
    actions: "mt-auto flex flex-col gap-[8px] pt-[24px]",
    button: cn(
      "font-poppins font-semibold leading-[1.4] text-[14px] min-[1430px]:text-[16px]",
      "flex w-full items-center justify-center rounded-[8px] px-[14px] py-[9px]",
      "bg-[var(--lf-primary)] text-[var(--lf-on-primary)] transition-opacity",
    ),
    buttonDisabled: "cursor-not-allowed opacity-50",
    secondaryButton: cn(
      "font-poppins font-medium text-[14px] leading-[1.4]",
      "w-full rounded-[8px] border border-[var(--lf-field-border)] bg-white px-[14px] py-[9px] text-[var(--lf-field-text)]",
      "transition-colors hover:bg-[#fafafa] disabled:opacity-50",
    ),
    consent: cn(
      "font-poppins font-normal text-center text-[var(--lf-consent)]",
      "text-[10px] min-[1230px]:text-[12px]",
    ),
    consentLink: "underline",

    /* ── OTP gate ───────────────────────────────────────────────────────────────── */
    otpBlock: "flex flex-col gap-[16px]",
    otpDescription: cn(FIELD_TEXT, "text-left text-[var(--lf-otp-desc)]"),
    otpInlineAction: "font-semibold underline cursor-pointer disabled:cursor-default",
    otpHeader: cn(
      "font-poppins font-semibold leading-[1.5] text-[var(--lf-otp-header)]",
      "text-[16px] min-[1430px]:text-[18px] min-[1750px]:text-[20px]",
    ),
    otpBoxes: "flex items-center gap-[8px]",
    otpBox: cn(
      "size-[44px] min-[1430px]:size-[50px] rounded-[4px] border bg-white text-center",
      "font-wf-visual-sans text-[16px] font-semibold text-[var(--lf-field-text)]",
      "border-[var(--lf-field-stroke,var(--lf-field-border))] outline-none",
      "focus:ring-1 focus:ring-[var(--lf-field-focus,var(--lf-primary))]",
      "disabled:opacity-60",
    ),
    otpBoxInvalid: "[--lf-field-stroke:#ef4444] [--lf-field-focus:#ef4444]",
    // Fixed muted grey, exactly as Leaddrop9 hardcodes it. Deliberately NOT tied to the
    // sent-to line's colour knob: an author recolouring that sentence does not expect the
    // resend line to follow it.
    otpResend: cn(FIELD_TEXT, "text-left text-[#888888]"),

    banner: cn(
      "font-poppins rounded-[7px] border border-red-200 bg-red-50 px-[12px] py-[10px]",
      "text-[12px] leading-[1.5] text-red-700",
    ),

    /* ── Terminal screen ────────────────────────────────────────────────────────── */
    successCard: cn(
      // Width comes through a VARIABLE so the popup can widen it without a second `w-*`
      // utility on the same element — two of those resolve by CSS source order, not by the
      // order they are written here, which makes the override a coin flip.
      // `overflow-x-hidden` rather than the shorthand: `successCardPopup` adds
      // `overflow-y-auto` onto this same element, and shorthand vs longhand for one axis
      // resolves by stylesheet order.
      "relative flex w-[var(--lf-ending-w,100%)] flex-col items-center overflow-x-hidden rounded-[12px] p-[30px]",
      "bg-[var(--lf-card-bg)]",
      // Same width ladder as the form card: the ending must not be a different size from
      // the screen the visitor was just looking at.
      "mx-auto max-w-[360px] min-[1080px]:max-w-[380px] min-[1230px]:max-w-[400px] min-[1430px]:max-w-[420px] min-[1750px]:max-w-[480px]",
      // The floor is what gives the illustration room to overlap the arc. Capped to the
      // viewport in the SAME declaration rather than overridden by a popup-only class —
      // two `min-h-*` utilities on one element resolve by CSS source order, not by the
      // order they are written in the attribute, so the override would be a coin flip.
      "min-h-[min(560px,calc(100dvh-2rem))] min-[1430px]:min-h-[min(596px,calc(100dvh-2rem))]",
    ),
    /**
     * The ending REPLACES the shell, so it has to carry the shell's width itself.
     *
     * Without an explicit width this was `w-full` inside a `w-fit` dialog — a circular
     * constraint that resolves to the width of the CONTENT. A short thank-you sentence
     * therefore produced a dramatically narrower popup than the form the visitor had just
     * been looking at. `shrink-0` for the same reason as `popupShell`: as a flex child it
     * would otherwise be squeezed below whatever width it asked for.
     */
    /**
     * Unused: skin 3000's ending REPLACES the shell (`endingKeepsShellChrome` is off), so
     * this never renders. Present because the contract requires it, and kept equivalent to
     * the card's inner layout so turning the flag on would degrade sensibly rather than
     * collapse.
     */
    successContentBare: "flex w-full flex-1 flex-col items-center gap-[28px]",
    successCountdown: "font-poppins text-center text-[12px] text-[#6b6b6b] [&_b]:font-semibold",
    successCardPopup: cn(
      "[--lf-ending-w:calc(100vw-2rem)] shrink-0",
      "max-h-[calc(100dvh-2rem)] overflow-y-auto",
    ),
    successImage:
      "relative z-[1] mt-[25px] h-[200px] w-[240px] shrink-0 min-[1430px]:h-[220px] min-[1430px]:w-[261px]",
    successContent: "z-[1] flex flex-1 flex-col items-center justify-end gap-[28px] pb-[10px]",
    successHeadings: "flex flex-col items-center gap-[4px]",
    successTitle: cn(
      "font-poppins font-semibold leading-[1.3] text-center",
      "text-[24px] tracking-[-0.24px]",
      "min-[1430px]:text-[28px] min-[1430px]:tracking-[-0.28px]",
      "min-[1750px]:text-[32px] min-[1750px]:tracking-[-0.32px]",
    ),
    successSubtitle: LAST_BODY,
    successBody: LAST_BODY,
    successButton: cn(
      "flex cursor-pointer items-center gap-[10px] rounded-[50px] border bg-transparent px-[20px] py-[10px]",
      "font-wf-visual-sans text-[14px] font-semibold transition-opacity hover:opacity-90",
      // The outcome's colour arrives as `--lf-success-cta`; this skin's ending CTA is an
      // outlined pill, so it takes both the border and the label.
      "border-[var(--lf-success-cta)] text-[var(--lf-success-cta)]",
    ),

    stepMeta: "text-center font-poppins text-[11px] font-medium leading-[1.4] text-[#8a8a8a]",
    radioLabel: "flex items-center gap-2 font-poppins text-[13px] text-[var(--lf-field-text)]",
  },
};

export default skin3000;
